import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlane, FaHotel, FaUmbrella, FaPassport, FaGlobeAmericas } from 'react-icons/fa';
import Loader from '../components/Loader';
import UnifiedStarBackground from '../components/UnifiedStarBackground';
import CurtainAnimation from '../components/CurtainAnimation';
import AnimatedIntro from '../components/AnimatedIntro';
import { supabase } from '../config/supabaseClient';
import '../styles/Login.css';
import '../styles/LoginLoader.css';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showCurtain, setShowCurtain] = useState(false);
  const [isCurtainOpen, setIsCurtainOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      const duration = 2500; // 2.5 seconds for loading
      const interval = 50; // Update every 50ms
      const steps = duration / interval;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = Math.min((currentStep / steps) * 100, 100);
        setLoadingProgress(progress);
        console.log('Loading progress:', progress); // Debug log
      }, interval);

      return () => clearInterval(timer);
    }
  }, [isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      if (data?.user) {
        console.log('Login successful, triggering animation sequence');
        // Let App component handle the animation sequence
        onLogin();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred during login');
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Loader */}
      <div className="login-loader" style={{ 
        display: isLoading ? 'flex' : 'none',
        opacity: isLoading ? 1 : 0
      }}>
        <div className="loader-content">
          <div className="loader-spinner" style={{ '--progress': `${loadingProgress}%` } as React.CSSProperties}></div>
          <p>Welcome to TripGo... {Math.round(loadingProgress)}%</p>
        </div>
      </div>

      {/* Curtain Animation */}
      {showCurtain && <CurtainAnimation isOpen={isCurtainOpen} />}
      
      {/* Intro Animation */}
      {showIntro && <AnimatedIntro onComplete={() => {
        onLogin();
        navigate('/');
      }} />}
      <div className="login-container">
        <UnifiedStarBackground variant="primary" />
      <div className="travel-icons">
        <FaPlane className="travel-icon" />
        <FaHotel className="travel-icon" />
        <FaGlobeAmericas className="travel-icon main-icon" />
        <FaUmbrella className="travel-icon" />
        <FaPassport className="travel-icon" />
      </div>
      <div className="login-box">
        <h2>
          <span className="title-trip">Trip</span>
          <span className="title-go">Go</span>
        </h2>
        <p className="subtitle">Your Journey Begins Here</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          <button type="submit" className="login-button">
            Login
          </button>
        </form>
        <p className="signup-link">
          Don't have an account? <a href="/signup">Sign up</a>
        </p>
      </div>
    </div>
    </>
  );
};

export default Login;